import { useState, useEffect, useCallback, useRef } from "react";
import { useAuth } from "../contexts/AuthContext";
import api, { formatApiError } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { toast } from "sonner";
import { ArrowLeft, AlertTriangle, Check, Link2, Trash2, Search } from "lucide-react";
import { useNavigate } from "react-router-dom";

function SkuSearchDropdown({ onSelect, disabled }) {
  const [search, setSearch] = useState("");
  const [results, setResults] = useState([]);
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    if (search.length < 2) { setResults([]); return; }
    const t = setTimeout(() => {
      api.get(`/platform-sku-map/orbit-skus?search=${encodeURIComponent(search)}`)
        .then(r => { setResults(r.data || []); setOpen(true); })
        .catch(() => {});
    }, 250);
    return () => clearTimeout(t);
  }, [search]);

  useEffect(() => {
    const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  return (
    <div className="relative" ref={ref} data-testid="sku-search-dropdown">
      <div className="flex items-center border border-zinc-200 rounded-sm h-8 px-2 bg-white">
        <Search size={12} className="text-zinc-400 mr-1.5 shrink-0" />
        <input
          type="text"
          value={search}
          onChange={e => setSearch(e.target.value)}
          placeholder="Type SKU name or ID..."
          className="text-xs w-full bg-transparent outline-none placeholder:text-zinc-400"
          disabled={disabled}
          data-testid="sku-search-input"
          onFocus={() => { if (results.length > 0) setOpen(true); }}
        />
      </div>
      {open && results.length > 0 && (
        <div className="absolute z-50 w-full mt-0.5 bg-white border border-zinc-200 rounded-sm shadow-lg max-h-48 overflow-y-auto" data-testid="sku-search-results">
          {results.map(s => (
            <button
              key={s.sku_id}
              className="w-full text-left px-2.5 py-1.5 text-xs hover:bg-zinc-100 border-b border-zinc-50 last:border-0"
              onClick={() => { onSelect(s); setSearch(`${s.sku_id} — ${s.sku_name}`); setOpen(false); }}
              data-testid={`sku-option-${s.sku_id}`}
            >
              <span className="font-mono text-zinc-500 mr-1.5">{s.sku_id}</span>
              <span className="text-zinc-800">{s.sku_name}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

export default function PlatformSkuMappingPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [platforms, setPlatforms] = useState([]);
  const [platform, setPlatform] = useState("");
  const [unmapped, setUnmapped] = useState([]);
  const [mappings, setMappings] = useState([]);
  const [selections, setSelections] = useState({});
  const [saving, setSaving] = useState({});
  const [tab, setTab] = useState("unmapped");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    api.get("/platform-sku-map/platforms")
      .then(r => {
        const list = r.data || [];
        setPlatforms(list);
        if (list.length > 0 && !platform) setPlatform(list[0].value.toLowerCase());
      })
      .catch(e => toast.error(formatApiError(e.response?.data?.detail)));
  }, []);

  const loadData = useCallback(() => {
    if (!platform) return;
    setLoading(true);
    const p = platform.toLowerCase();
    Promise.all([
      api.get(`/platform-sku-map/unmapped?platform=${encodeURIComponent(p)}`),
      api.get(`/platform-sku-map/mappings?platform=${encodeURIComponent(p)}`),
    ]).then(([u, m]) => {
      setUnmapped(u.data || []);
      setMappings(m.data || []);
    }).catch(e => toast.error(formatApiError(e.response?.data?.detail)))
      .finally(() => setLoading(false));
  }, [platform]);

  useEffect(() => { loadData(); }, [loadData]);

  const handleMap = async (item) => {
    const sel = selections[item.platform_item_id];
    if (!sel) { toast.error("Select an ORBIT SKU first"); return; }
    setSaving(s => ({ ...s, [item.platform_item_id]: true }));
    try {
      await api.post("/platform-sku-map/map", {
        platform: platform.toLowerCase(),
        platform_item_id: item.platform_item_id,
        platform_item_name: item.platform_item_name,
        orbit_sku_id: sel.sku_id,
      });
      toast.success(`Mapped ${item.platform_item_id} → ${sel.sku_id}`);
      setSelections(s => { const n = { ...s }; delete n[item.platform_item_id]; return n; });
      loadData();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    setSaving(s => ({ ...s, [item.platform_item_id]: false }));
  };

  const handleUnmap = async (m) => {
    if (!window.confirm(`Remove mapping for ${m.platform_item_id}?`)) return;
    try {
      await api.post("/platform-sku-map/unmap", {
        platform: m.platform,
        platform_item_id: m.platform_item_id,
      });
      toast.success("Mapping removed");
      loadData();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  return (
    <div data-testid="platform-sku-mapping-page">
      <div className="flex items-center gap-3 mb-6">
        <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => navigate("/ss-inventory")}>
          <ArrowLeft size={16} />
        </Button>
        <div>
          <h1 className="font-heading text-2xl font-bold tracking-tight text-zinc-950">Platform SKU Mapping</h1>
          <p className="text-sm text-zinc-500">
            Map platform item IDs to ORBIT catalogue SKUs. Mapping is by <strong>item_id only</strong> — names are display-only.
          </p>
        </div>
      </div>

      {/* Platform selector */}
      <div className="flex items-center gap-4 mb-6" data-testid="platform-selector">
        <label className="text-xs font-semibold text-zinc-600">Platform:</label>
        <Select value={platform} onValueChange={v => setPlatform(v)}>
          <SelectTrigger className="w-52 h-8 text-xs rounded-sm" data-testid="platform-dropdown">
            <SelectValue placeholder="Select platform..." />
          </SelectTrigger>
          <SelectContent>
            {platforms.map(p => (
              <SelectItem key={p.value} value={p.value.toLowerCase()}>{p.label}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 border-b border-zinc-200 mb-4" data-testid="mapping-tabs">
        <button
          className={`px-3 py-1.5 text-xs font-medium border-b-2 transition-colors ${tab === "unmapped" ? "border-zinc-900 text-zinc-900" : "border-transparent text-zinc-400 hover:text-zinc-600"}`}
          onClick={() => setTab("unmapped")}
          data-testid="tab-unmapped"
        >
          Unmapped ({unmapped.length})
        </button>
        <button
          className={`px-3 py-1.5 text-xs font-medium border-b-2 transition-colors ${tab === "mapped" ? "border-zinc-900 text-zinc-900" : "border-transparent text-zinc-400 hover:text-zinc-600"}`}
          onClick={() => setTab("mapped")}
          data-testid="tab-mapped"
        >
          All Mappings ({mappings.length})
        </button>
      </div>

      {loading && <p className="text-xs text-zinc-400 py-4">Loading...</p>}

      {/* Unmapped Tab */}
      {tab === "unmapped" && !loading && (
        <div data-testid="unmapped-list">
          {unmapped.length === 0 ? (
            <div className="text-center py-12 text-zinc-400 text-xs border border-dashed border-zinc-200 rounded-sm">
              All platform SKUs for <strong>{platform}</strong> are mapped.
            </div>
          ) : (
            <div className="border border-amber-200 rounded-sm bg-amber-50/50">
              <div className="px-4 py-2 border-b border-amber-200 flex items-center gap-2">
                <AlertTriangle size={14} className="text-amber-600" />
                <h2 className="text-sm font-semibold text-amber-800">
                  Unmapped Platform SKUs — {unmapped.length} items need mapping
                </h2>
              </div>
              <div className="divide-y divide-amber-100">
                {unmapped.map(item => (
                  <div key={item.platform_item_id} className="flex items-center gap-3 px-4 py-2.5" data-testid={`unmapped-row-${item.platform_item_id}`}>
                    <div className="flex-1 min-w-0">
                      <p className="text-xs font-semibold text-zinc-900 truncate">{item.platform_item_name || "(no name)"}</p>
                      <p className="text-[10px] text-zinc-500 font-mono">
                        ID: {item.platform_item_id}
                        {item.sample_facility ? ` · ${item.sample_facility}` : ""}
                        {" · "}{item.total_grn_qty} GRN qty · {item.po_count} POs
                      </p>
                    </div>
                    <div className="w-64 shrink-0">
                      <SkuSearchDropdown
                        onSelect={s => setSelections(prev => ({ ...prev, [item.platform_item_id]: s }))}
                        disabled={saving[item.platform_item_id]}
                      />
                    </div>
                    <Button
                      size="sm"
                      className="h-7 text-[10px] rounded-sm shrink-0"
                      disabled={!selections[item.platform_item_id] || saving[item.platform_item_id]}
                      onClick={() => handleMap(item)}
                      data-testid={`map-btn-${item.platform_item_id}`}
                    >
                      <Check size={12} className="mr-1" />
                      {saving[item.platform_item_id] ? "..." : "Map"}
                    </Button>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Mapped Tab */}
      {tab === "mapped" && !loading && (
        <div className="border border-zinc-200 rounded-sm" data-testid="mapped-list">
          <div className="px-4 py-2 bg-zinc-50 border-b border-zinc-200">
            <h2 className="text-sm font-semibold text-zinc-700">
              All Mappings for {platform} ({mappings.length})
            </h2>
          </div>
          <table className="data-table w-full">
            <thead>
              <tr>
                <th>Platform Item ID</th>
                <th>Platform Item Name</th>
                <th>ORBIT SKU ID</th>
                <th>ORBIT SKU Name</th>
                <th>Mapped By</th>
                <th>Date</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {mappings.length === 0 ? (
                <tr>
                  <td colSpan={7} className="text-center py-8 text-zinc-400 text-xs">
                    No mappings for {platform} yet.
                  </td>
                </tr>
              ) : mappings.map(m => (
                <tr key={m._id} data-testid={`mapping-row-${m.platform_item_id}`}>
                  <td className="text-xs font-mono">{m.platform_item_id}</td>
                  <td className="text-xs text-zinc-600 max-w-[200px] truncate">{m.platform_item_name || "—"}</td>
                  <td className="text-xs font-mono font-semibold text-emerald-700">{m.orbit_sku_id}</td>
                  <td className="text-xs text-zinc-600 max-w-[200px] truncate">{m.orbit_sku_name || "—"}</td>
                  <td className="text-xs text-zinc-500">{m.mapped_by_name || "—"}</td>
                  <td className="text-xs text-zinc-400 font-mono">{(m.mapped_at || "").slice(0, 10)}</td>
                  <td>
                    <Button
                      variant="ghost"
                      size="sm"
                      className="h-6 w-6 p-0 text-red-500 hover:text-red-700 hover:bg-red-50"
                      onClick={() => handleUnmap(m)}
                      data-testid={`unmap-btn-${m.platform_item_id}`}
                    >
                      <Trash2 size={12} />
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
